/**
 * locationManager.js — single source of truth for active weather location.
 *
 * Rules
 * -----
 * 1. Only TWO things write to localStorage("weatherLocation"):
 *      a. activateCurrentLocation()  — GPS allow
 *      b. applyDefaultLocation()     — fallback / first-run
 * 2. Forecast city search is NEVER stored here (stays in hook local state).
 * 3. No IP-geolocation fallback (privacy requirement).
 */

// ─── Constants ────────────────────────────────────────────────────────────────

export const STORAGE_KEY = 'weatherLocation';

export const DEFAULT_LOCATION = {
  name: 'Minneapolis, MN (55401), USA',
  city: 'Minneapolis',
  region: 'Minnesota',
  country: 'United States',
  countryCode: 'US',
  lat: 44.9777,
  lng: -93.276,
  liveLocation: false,
};

const LOCATION_CHANGED_EVENT = 'weather-location-changed';

// ─── Validation ───────────────────────────────────────────────────────────────

function isValidLocation(loc) {
  if (!loc || typeof loc !== 'object') return false;
  const lat = Number(loc.lat);
  const lng = Number(loc.lng);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

// ─── Core read / write ────────────────────────────────────────────────────────

/** Read the active location from localStorage; falls back to DEFAULT_LOCATION. */
export function getActiveLocation() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (isValidLocation(parsed)) return parsed;
    }
  } catch {
    // ignore parse errors
  }
  return { ...DEFAULT_LOCATION };
}

/** Persist a location as the active global location and notify all listeners. */
export function setActiveLocation(loc) {
  if (!isValidLocation(loc)) {
    console.warn('[locationManager] setActiveLocation called with invalid location', loc);
    return;
  }
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch (e) {
    console.error('[locationManager] localStorage write failed', e);
  }
  window.dispatchEvent(new CustomEvent(LOCATION_CHANGED_EVENT, { detail: loc }));
}

/** Remove saved location and reset to default. */
export function clearActiveLocation() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {}
  window.dispatchEvent(new CustomEvent(LOCATION_CHANGED_EVENT, { detail: { ...DEFAULT_LOCATION } }));
}

/** Save Minneapolis as the active location (first-run / fallback). */
export function applyDefaultLocation() {
  const loc = { ...DEFAULT_LOCATION };
  setActiveLocation(loc);
  return loc;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function isDefaultLocation(loc) {
  if (!loc) return true;
  return (
    Number(loc.lat) === DEFAULT_LOCATION.lat &&
    Number(loc.lng) === DEFAULT_LOCATION.lng
  );
}

export function isLiveLocation(loc) {
  return !!loc?.liveLocation && !isDefaultLocation(loc);
}

/** Human-readable label for header / UI. */
export function getLocationDisplayName(loc) {
  if (!loc) return DEFAULT_LOCATION.name;
  if (loc.city && loc.region) return `${loc.city}, ${loc.region}`;
  if (loc.city && loc.country) return `${loc.city}, ${loc.country}`;
  if (loc.name) return loc.name;
  return DEFAULT_LOCATION.name;
}

/**
 * Build the location string expected by the Aeris forecast API.
 * Returns "lat,lng" when coordinates are available, "city,countryCode," otherwise.
 */
export function toForecastApiString(loc) {
  if (!loc) return `${DEFAULT_LOCATION.lat},${DEFAULT_LOCATION.lng}`;
  if (loc.lat != null && loc.lng != null) return `${loc.lat},${loc.lng}`;
  if (loc.city && loc.countryCode) {
    return `${loc.city.toLowerCase()},${loc.countryCode.toLowerCase()}`;
  }
  if (loc.name) return `${loc.name},`;
  return `${DEFAULT_LOCATION.lat},${DEFAULT_LOCATION.lng}`;
}

// ─── Geolocation + Reverse geocode ────────────────────────────────────────────

/** @type {Promise<GeolocationPosition> | null} */
let pendingPositionRequest = null;

/**
 * Query Permissions API when available (Chrome/Edge/Firefox).
 * @returns {'granted'|'denied'|'prompt'|'unknown'}
 */
export async function getGeolocationPermissionState() {
  if (typeof navigator === 'undefined' || !navigator.permissions?.query) {
    return 'unknown';
  }
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' });
    return status.state;
  } catch {
    return 'unknown';
  }
}

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function getCurrentPositionOnce(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

/**
 * Chrome/Linux often returns code 1 on the first call even when permission is granted;
 * a short delayed retry usually succeeds (same as pasting getCurrentPosition twice in console).
 */
async function getCurrentPositionWithRetry(options, maxAttempts = 4) {
  let lastErr;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      if (attempt > 0) await delay(400 * attempt);
      return await getCurrentPositionOnce(options);
    } catch (err) {
      lastErr = err;
      if (err?.code !== 1 && err?.code !== 3) throw err;
    }
  }
  throw lastErr;
}

/** watchPosition fallback — some Linux/desktop setups fail getCurrentPosition but accept watch. */
function watchPositionOnce(options) {
  const timeoutMs = options.timeout ?? 30000;
  return new Promise((resolve, reject) => {
    let watchId = null;
    let settled = false;

    const finish = (fn, arg) => {
      if (settled) return;
      settled = true;
      if (watchId != null) navigator.geolocation.clearWatch(watchId);
      clearTimeout(timer);
      fn(arg);
    };

    watchId = navigator.geolocation.watchPosition(
      (pos) => finish(resolve, pos),
      (err) => finish(reject, err),
      { ...options, timeout: undefined }
    );

    const timer = setTimeout(() => {
      finish(reject, Object.assign(new Error('Location request timed out'), { code: 3 }));
    }, timeoutMs);
  });
}

async function tryPositionStrategies() {
  const lowAccuracy = {
    enableHighAccuracy: false,
    maximumAge: 60000,
    timeout: 25000,
  };

  try {
    return await getCurrentPositionWithRetry(lowAccuracy, 4);
  } catch (firstErr) {
    try {
      return await watchPositionOnce(lowAccuracy);
    } catch {
      throw firstErr;
    }
  }
}

/**
 * Wrap navigator.geolocation in a promise.
 * Rejects with GeolocationPositionError (code 1 = denied, 2 = unavailable, 3 = timeout).
 */
export function requestBrowserPosition() {
  if (pendingPositionRequest) return pendingPositionRequest;

  pendingPositionRequest = (async () => {
    if (typeof window !== 'undefined' && !window.isSecureContext) {
      throw Object.assign(
        new Error('Location access requires HTTPS. Please open the site using https://'),
        { code: 0 }
      );
    }
    if (!navigator.geolocation) {
      throw new Error('Geolocation is not supported by this browser');
    }
    return tryPositionStrategies();
  })().finally(() => {
    pendingPositionRequest = null;
  });

  return pendingPositionRequest;
}

/** User-facing message when code === 1, accounting for browser vs OS blocks. */
export async function geolocationErrorMessage(err) {
  const permission = await getGeolocationPermissionState();

  if (err?.code === 3) {
    return 'Location request timed out. Check that device location services are on, then try again.';
  }
  if (err?.code === 2) {
    return 'Location unavailable on this device. Try again or use the default city.';
  }
  if (err?.code === 1) {
    if (permission === 'granted') {
      return (
        'Browser allows location, but the device could not provide coordinates. ' +
        'On Linux: open System Settings → Privacy → Location, turn Location Services ON, ' +
        'then fully quit and reopen the browser. On Windows/Mac: enable location for your user account.'
      );
    }
    if (permission === 'denied') {
      return 'Location is blocked for this site. Click the lock icon in the address bar → Site settings → Location → Allow, then reload.';
    }
    return 'Location access denied. Allow location when prompted, or enable it in browser site settings, then reload.';
  }
  return err?.message || 'Could not get device location.';
}

/**
 * Reverse-geocode lat/lng via OpenStreetMap Nominatim.
 * Returns partial location fields; falls back to coordinate-only if the call fails.
 */
export async function reverseGeocode(lat, lng) {
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`;
    const res = await fetch(url, {
      headers: { 'Accept-Language': 'en', 'User-Agent': 'WeatherAgencyApp/1.0' },
    });
    if (!res.ok) throw new Error(`Nominatim ${res.status}`);
    const data = await res.json();
    const addr = data.address || {};
    const city =
      addr.city || addr.town || addr.village || addr.municipality || addr.county || '';
    const region = addr.state || addr.region || '';
    const country = addr.country || '';
    const countryCode = (addr.country_code || '').toUpperCase();
    const name = [city, region || country].filter(Boolean).join(', ') || data.display_name || 'Current Location';
    return { city, region, country, countryCode, name };
  } catch (e) {
    console.warn('[locationManager] reverseGeocode failed, using coordinate-only name', e);
    return { city: '', region: '', country: '', countryCode: '', name: 'Current Location' };
  }
}

/** Apply a GeolocationPosition to active location (save + reverse geocode). */
export async function applyPositionToActiveLocation(position) {
  const { latitude, longitude } = position.coords;

  const coordsOnly = {
    lat: latitude,
    lng: longitude,
    liveLocation: true,
    name: 'Current Location',
    city: '',
    region: '',
    country: '',
    countryCode: '',
  };
  setActiveLocation(coordsOnly);

  const geocoded = await reverseGeocode(latitude, longitude);
  const loc = {
    ...geocoded,
    lat: latitude,
    lng: longitude,
    liveLocation: true,
  };
  setActiveLocation(loc);
  return loc;
}

/**
 * Full flow: GPS → reverse geocode → persist as active location → return location.
 * Call requestBrowserPosition() synchronously from a click handler before any await.
 */
export async function activateCurrentLocation() {
  const position = await requestBrowserPosition();
  return applyPositionToActiveLocation(position);
}

// ─── Cross-component event subscription ───────────────────────────────────────

/**
 * Subscribe to active-location changes (both same-tab events and cross-tab storage events).
 * Returns an unsubscribe function.
 *
 * @param {(location: object) => void} callback
 */
export function subscribeLocationChange(callback) {
  const onCustom = (e) => callback(e.detail);
  const onStorage = (e) => {
    if (e.key === STORAGE_KEY) {
      callback(getActiveLocation());
    }
  };
  window.addEventListener(LOCATION_CHANGED_EVENT, onCustom);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(LOCATION_CHANGED_EVENT, onCustom);
    window.removeEventListener('storage', onStorage);
  };
}
